import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { listAccounts, type Account } from '../../api/accounts';
import type { AddModelData } from '../../api/models';

interface ModelFormProps {
  onSubmit: (data: AddModelData) => Promise<void>;
  onCancel: () => void;
}

const MODEL_PRESETS = [
  {
    label: 'Claude Sonnet 4 (Bedrock)',
    modelName: 'claude-sonnet',
    litellmModel: 'bedrock/us.anthropic.claude-sonnet-4-20250514-v1:0',
  },
  {
    label: 'Claude Haiku 3.5 (Bedrock)',
    modelName: 'claude-haiku',
    litellmModel: 'bedrock/us.anthropic.claude-3-5-haiku-20241022-v1:0',
  },
  {
    label: 'Claude Fable 5 (Bedrock Global)',
    modelName: 'claude-fable-5',
    litellmModel: 'bedrock/global.anthropic.claude-fable-5',
  },
  {
    label: 'Claude Opus 4.8 (Bedrock Global)',
    modelName: 'claude-opus-4-8',
    litellmModel: 'bedrock/global.anthropic.claude-opus-4-8',
  },
  {
    label: 'Claude Opus 4.7 (Bedrock US)',
    modelName: 'claude-opus-4-7',
    litellmModel: 'bedrock/us.anthropic.claude-opus-4-7',
  },
  {
    label: 'Claude Opus 4.6 (Bedrock Global)',
    modelName: 'claude-opus-4-6',
    litellmModel: 'bedrock/global.anthropic.claude-opus-4-6-v1',
  },
  {
    label: 'Nova Pro (Bedrock)',
    modelName: 'nova-pro',
    litellmModel: 'bedrock/us.amazon.nova-pro-v1:0',
  },
];

export function ModelForm({ onSubmit, onCancel }: ModelFormProps) {
  const { getJwtToken } = useAuth();

  const [modelName, setModelName] = useState('');
  const [litellmModel, setLitellmModel] = useState('');
  const [apiBase, setApiBase] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [accountId, setAccountId] = useState('');
  const [tpm, setTpm] = useState('');
  const [rpm, setRpm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load accounts for the selector
  const [accounts, setAccounts] = useState<Account[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const token = await getJwtToken();
        if (!token) return;
        const data = await listAccounts(token);
        setAccounts(data.filter((a) => a.status === 'active'));
      } catch {
        // Non-critical, account selector will just show "Local" option
      }
    })();
  }, [getJwtToken]);

  const handlePreset = (preset: typeof MODEL_PRESETS[number]) => {
    setModelName(preset.modelName);
    setLitellmModel(preset.litellmModel);
    setApiBase('');
    setApiKey('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    if (!modelName.trim() || !litellmModel.trim()) {
      setError('Model Name and Provider Model ID are required.');
      setSubmitting(false);
      return;
    }

    try {
      const data: AddModelData = {
        modelName: modelName.trim(),
        litellmModel: litellmModel.trim(),
      };
      if (apiBase.trim()) data.apiBase = apiBase.trim();
      if (apiKey.trim()) data.apiKey = apiKey.trim();
      if (accountId) data.accountId = accountId;
      if (tpm) data.tpm = parseInt(tpm, 10);
      if (rpm) data.rpm = parseInt(rpm, 10);

      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add model');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="alert alert--error" style={{ marginBottom: 16 }}>
          <span>{error}</span>
        </div>
      )}

      <div className="modal__field">
        <label htmlFor="account-select">Account</label>
        <select
          id="account-select"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          <option value="">Local (this account)</option>
          {accounts.map((a) => (
            <option key={a.accountId} value={a.accountId}>
              {a.accountName} ({a.accountId} - {a.region})
            </option>
          ))}
        </select>
        <small className="modal__hint">
          Select which AWS account to use for Bedrock. "Local" uses the gateway's own credentials.
        </small>
      </div>

      <div className="model-form__presets">
        <label className="model-form__presets-label">Quick presets:</label>
        <div className="model-form__presets-list">
          {MODEL_PRESETS.map((preset) => (
            <button
              key={preset.litellmModel}
              type="button"
              className="model-form__preset-btn"
              onClick={() => handlePreset(preset)}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="modal__field">
        <label htmlFor="model-name">Model Name (user-facing alias)</label>
        <input
          id="model-name"
          type="text"
          value={modelName}
          onChange={(e) => setModelName(e.target.value)}
          placeholder="e.g. claude-haiku"
          required
          autoFocus
        />
        <small className="modal__hint">
          The name clients will use to reference this model in API requests.
        </small>
      </div>

      <div className="modal__field">
        <label htmlFor="litellm-model">Provider Model ID</label>
        <input
          id="litellm-model"
          type="text"
          value={litellmModel}
          onChange={(e) => setLitellmModel(e.target.value)}
          placeholder="e.g. bedrock/us.anthropic.claude-sonnet-4-20250514-v1:0"
          required
        />
        <small className="modal__hint">
          Format: provider/model-id (e.g. bedrock/..., openai/gpt-4o, azure/...)
        </small>
      </div>

      <div className="modal__field">
        <label htmlFor="api-base">API Base URL (optional)</label>
        <input
          id="api-base"
          type="text"
          value={apiBase}
          onChange={(e) => setApiBase(e.target.value)}
          placeholder="https://api.openai.com/v1"
        />
        <small className="modal__hint">
          Only needed for non-Bedrock providers or custom endpoints.
        </small>
      </div>

      <div className="modal__field">
        <label htmlFor="api-key">API Key (optional)</label>
        <input
          id="api-key"
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="sk-..."
        />
        <small className="modal__hint">
          Only needed for providers that require an API key (e.g. OpenAI). Bedrock models use IAM credentials automatically.
        </small>
      </div>

      <div className="modal__field">
        <label htmlFor="tpm-limit">TPM Limit (optional)</label>
        <input
          id="tpm-limit"
          type="number"
          value={tpm}
          onChange={(e) => setTpm(e.target.value)}
          placeholder="e.g. 1000000"
          min={0}
        />
        <small className="modal__hint">
          Tokens per minute limit for this model deployment. Used by LiteLLM for load balancing.
        </small>
      </div>

      <div className="modal__field">
        <label htmlFor="rpm-limit">RPM Limit (optional)</label>
        <input
          id="rpm-limit"
          type="number"
          value={rpm}
          onChange={(e) => setRpm(e.target.value)}
          placeholder="e.g. 1000"
          min={0}
        />
        <small className="modal__hint">
          Requests per minute limit for this model deployment. Used by LiteLLM for load balancing.
        </small>
      </div>

      <div className="modal__actions">
        <button type="button" className="modal__cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="modal__submit" disabled={submitting}>
          {submitting ? 'Adding...' : 'Add Model'}
        </button>
      </div>
    </form>
  );
}
